import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Col,
  Container,
  Row,
  Card,
  CardBody,
  Spinner,
  Input,
  Badge,
  Nav,
  NavItem,
  NavLink,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import withAuth from "../withAuth";
import axios from "axios";
import { toast } from "react-toastify";
import { baseUrl } from "../../Network";
import { handleAuthError, newFormatDate } from "../../utils";
import PaginationControls from "../../components/Common/PaginationControls";
import FilterBarCollapsible from "../../components/Common/FilterBarCollapsible";

const formatDuration = (seconds) => {
  const s = Math.max(0, Math.floor(Number(seconds) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const rem = s % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${rem}s`;
  return `${rem}s`;
};

const statusColor = (status) => {
  if (status === "completed") return "success";
  if (status === "ongoing") return "info";
  return "secondary";
};

const parentName = (row) =>
  `${row?.firstName || ""} ${row?.lastName || ""}`.trim() || "—";

const ManageAiMetrics = () => {
  document.title = "Manage Metrics | RYD Admin";
  const [tab, setTab] = useState("engagement");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20,
  });
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const path =
        tab === "not-started"
          ? "/admin/ai/metrics/not-started"
          : "/admin/ai/metrics/engagement";
      const res = await axios.get(`${baseUrl}${path}`, {
        params: {
          page: currentPage,
          limit: pageSize,
          search: search.trim() || undefined,
        },
      });
      if (res?.data?.status) {
        setItems(Array.isArray(res.data.data?.items) ? res.data.data.items : []);
        setPagination(
          res.data.data?.pagination || {
            currentPage: 1,
            totalPages: 1,
            totalItems: 0,
            itemsPerPage: pageSize,
          }
        );
      }
    } catch (e) {
      handleAuthError(e);
    } finally {
      setLoading(false);
    }
  }, [tab, currentPage, pageSize, search]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    load();
  };

  const csvEscape = (value) => {
    const text = String(value ?? "");
    if (/[",\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
    return text;
  };

  const exportNotStartedEmails = async () => {
    try {
      setExporting(true);
      const res = await axios.get(`${baseUrl}/admin/ai/metrics/not-started`, {
        params: {
          export: "emails",
          search: search.trim() || undefined,
        },
      });
      if (!res?.data?.status) {
        toast.error(res?.data?.message || "Unable to export emails");
        return;
      }
      const rows = Array.isArray(res.data.data?.items) ? res.data.data.items : [];
      const emails = Array.isArray(res.data.data?.emails)
        ? res.data.data.emails
        : rows.map((row) => row.email).filter(Boolean);

      if (!emails.length) {
        toast.warn("No emails to export");
        return;
      }

      const header = ["Email", "First Name", "Last Name", "Phone"];
      const lines = [
        header.join(","),
        ...rows.map((row) =>
          [row.email, row.firstName, row.lastName, row.phone]
            .map(csvEscape)
            .join(",")
        ),
      ];

      const blob = new Blob([lines.join("\n")], {
        type: "text/csv;charset=utf-8;",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 10);
      link.href = url;
      link.download = `ai-not-started-emails-${stamp}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${emails.length} email${emails.length === 1 ? "" : "s"}`);
    } catch (e) {
      handleAuthError(e);
      toast.error(e?.response?.data?.message || "Failed to export emails");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="AI" breadcrumbItem="Manage Metrics" />

        <Nav tabs className="mb-3">
          <NavItem>
            <NavLink
              href="#"
              className={tab === "engagement" ? "active" : ""}
              onClick={(e) => {
                e.preventDefault();
                setTab("engagement");
                setCurrentPage(1);
              }}
            >
              Course engagement
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              href="#"
              className={tab === "not-started" ? "active" : ""}
              onClick={(e) => {
                e.preventDefault();
                setTab("not-started");
                setCurrentPage(1);
              }}
            >
              Access, not started
            </NavLink>
          </NavItem>
        </Nav>

        <FilterBarCollapsible>
          <form onSubmit={handleSearch} className="row g-2 mb-3">
            <Col md={6}>
              <Input
                placeholder={
                  tab === "engagement"
                    ? "Search parent, email, or course..."
                    : "Search parent or email..."
                }
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Col>
            <Col md={2}>
              <button type="submit" className="btn btn-primary w-100">
                Search
              </button>
            </Col>
            {tab === "not-started" ? (
              <Col md={4} className="d-flex justify-content-md-end">
                <button
                  type="button"
                  className="btn btn-outline-success"
                  onClick={exportNotStartedEmails}
                  disabled={exporting || loading || pagination.totalItems === 0}
                >
                  {exporting ? (
                    <>
                      <Spinner size="sm" className="me-1" /> Exporting…
                    </>
                  ) : (
                    <>
                      <i className="mdi mdi-download me-1" />
                      Export all emails
                    </>
                  )}
                </button>
              </Col>
            ) : null}
          </form>
        </FilterBarCollapsible>

        {loading ? (
          <div className="text-center py-5">
            <Spinner />
          </div>
        ) : items.length === 0 ? (
          <Card>
            <CardBody>
              <p className="text-muted text-center mb-0 py-4">
                {tab === "not-started"
                  ? "No parents with AI access who have not started a course."
                  : "No course engagement yet."}
              </p>
            </CardBody>
          </Card>
        ) : tab === "not-started" ? (
          <Row>
            {items.map((row) => (
              <Col md={6} xl={4} key={row.parentId} className="mb-3">
                <Card className="h-100 shadow-sm">
                  <CardBody>
                    <div className="d-flex justify-content-between align-items-start gap-2">
                      <div>
                        <h5 className="mb-1">{parentName(row)}</h5>
                        <div className="text-muted">{row.email || "—"}</div>
                        {row.phone ? (
                          <small className="text-muted d-block">{row.phone}</small>
                        ) : null}
                      </div>
                      <Badge color="warning">Not started</Badge>
                    </div>
                    <div className="mt-3 pt-3 border-top">
                      <small className="text-muted d-block mb-2">
                        Has AI access but has not started any course.
                      </small>
                      <Link
                        to={`/ai/parents?search=${encodeURIComponent(row.email || "")}`}
                        className="btn btn-sm btn-outline-secondary"
                      >
                        View parent
                      </Link>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>
        ) : (
          <div className="d-flex flex-column gap-3">
            {items.map((row) => (
              <Card key={row.parentId} className="shadow-sm mb-0">
                <CardBody>
                  <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
                    <div>
                      <h5 className="mb-1">{parentName(row)}</h5>
                      <div className="text-muted">{row.email || "—"}</div>
                      {row.phone ? (
                        <small className="text-muted d-block">{row.phone}</small>
                      ) : null}
                    </div>
                    <div className="text-end">
                      <Badge color="primary" className="me-1">
                        {row.courseCount || row.courses?.length || 0} course
                        {(row.courseCount || row.courses?.length || 0) === 1 ? "" : "s"}
                      </Badge>
                      <div>
                        <Link
                          to={`/ai/parents?search=${encodeURIComponent(row.email || "")}`}
                          className="btn btn-sm btn-outline-secondary mt-2"
                        >
                          View parent
                        </Link>
                      </div>
                    </div>
                  </div>

                  <div className="d-flex flex-column gap-2">
                    {(row.courses || []).map((course) => (
                      <div
                        key={`${row.parentId}-${course.courseSlug}`}
                        className="rounded-3 p-3"
                        style={{ background: "#f8f9fb", border: "1px solid #eef0f4" }}
                      >
                        <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-2">
                          <div>
                            <div className="fw-semibold">{course.courseTitle}</div>
                            <small className="text-muted">{course.courseSlug}</small>
                          </div>
                          <Badge color={statusColor(course.status)}>
                            {course.status || "not-started"}
                          </Badge>
                        </div>
                        <Row className="g-2">
                          <Col xs={6} md={3}>
                            <small className="text-muted d-block">Times on course</small>
                            <strong>{course.sessionCount || 0}</strong>
                          </Col>
                          <Col xs={6} md={3}>
                            <small className="text-muted d-block">Total time spent</small>
                            <strong>{formatDuration(course.timeSpentSeconds)}</strong>
                          </Col>
                          <Col xs={6} md={3}>
                            <small className="text-muted d-block">Progress</small>
                            <strong>{Math.round(Number(course.progressPercent) || 0)}%</strong>
                          </Col>
                          <Col xs={6} md={3}>
                            <small className="text-muted d-block">Last activity</small>
                            <strong>
                              {course.lastActivityAt
                                ? newFormatDate(course.lastActivityAt)
                                : "—"}
                            </strong>
                          </Col>
                        </Row>
                      </div>
                    ))}
                  </div>
                </CardBody>
              </Card>
            ))}
          </div>
        )}

        {!loading && pagination.totalItems > 0 && (
          <div className="mt-3">
            <PaginationControls
              currentPage={currentPage}
              totalPages={pagination.totalPages}
              totalItems={pagination.totalItems}
              pageSize={pageSize}
              showAll={false}
              paginatedDataLength={items.length}
              handlePageChange={setCurrentPage}
              handlePageSizeChange={(size) => {
                const next = size === "all" ? 100 : Number(size) || 20;
                setPageSize(next);
                setCurrentPage(1);
              }}
              goToNextPage={() => setCurrentPage((p) => p + 1)}
              goToPreviousPage={() => setCurrentPage((p) => Math.max(1, p - 1))}
              canPreviousPage={currentPage > 1}
              canNextPage={currentPage < pagination.totalPages}
            />
          </div>
        )}
      </Container>
    </div>
  );
};

export default withAuth(ManageAiMetrics);
